import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  useMediaQuery,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { X, RefreshCw, ExternalLink, FileText, ArrowLeft } from "lucide-react";

import ArticleResources from "./ArticleResource";
import ShareArticle from "./ShareArticle";
import Loading from "../../../common/Loading";
import Button from "../../../common/Button";

import { useOrganisation } from "../../../../hooks/useOrganisation";
import { getArticleByid } from "../../../../store/api/content";
import {
  emailClickContentAnalytics,
  pushClickContentAnalytics,
  readContentAnalytics,
} from "../../../../store/api/analytics";
import { getTextColor } from "../../../../helpers";
import { IMAGE_URL_AZURE } from "../../../../constants/env";
import { getRole } from "../../../../helpers/auth";
import { roles } from "../../../../constants/routes";

const ZoomableCardMediaWithFallback = ({
  image,
  alt,
  height,
  title,
  primaryColor,
  isMobileCompact,
}) => {
  return (
    <Box
      sx={{
        overflow: "hidden",
        height: height || "auto",
        position: "relative",
      }}
    >
      {image ? (
        <CardMedia
          component="img"
          height={height}
          image={image}
          alt={alt}
          sx={{
            transition: "transform 0.5s ease",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        />
      ) : (
        <Box
          sx={{
            height: height || "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: primaryColor,
            position: "relative",
          }}
        >
          <Typography
            variant={isMobileCompact ? "body2" : "h6"}
            sx={{
              color: getTextColor(primaryColor),
              textAlign: "center",
              fontWeight: "bold",
              padding: 1,
              fontSize: isMobileCompact ? "0.75rem" : undefined,
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ErrorDisplay component remains the same
const ErrorDisplay = ({ message, onRetry, onGoHome }) => {
  const navigate = useNavigate();
  const companyName = localStorage.getItem("companyName");
  const { primaryColor } = useOrganisation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        textAlign: "center",
        padding: 4,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Oops! We couldn't find that article
      </Typography>
      <Typography variant="body1" paragraph>
        {message ||
          "The article you're looking for might have been removed, had its name changed, or is temporarily unavailable."}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, marginTop: 3 }}>
        <Button
          startIcon={<RefreshCw size={16} />}
          onClick={onRetry}
          backgroundColor="white"
          text="Try Again"
        />

        <Button
          onClick={() => navigate(`/${companyName}/dashboard`)}
          text="Browse Articles"
        />
      </Box>
    </Box>
  );
};

const ArticlePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const role = getRole();
  const ComingFrom = searchParams.get("ComingFrom");
  const isIframe = localStorage.getItem("isIframeMode");
  const companyName = localStorage.getItem("companyName");
  const { primaryColor, primaryColorLight } = useOrganisation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUrlDialog, setShowUrlDialog] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["article", id],
    queryFn: () => getArticleByid({ id }),
    enabled: !!id,
    retry: 1,
    onError: (error) => {
      console.error("Error fetching article:", error);
    },
  });

  const { data: analytics } = useQuery({
    queryKey: ["contentAnalytics", id],
    queryFn: () => readContentAnalytics({ contentId: id }),
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    enabled: role === roles.Reader && !isError && !!data,
  });

  const {} = useQuery({
    queryKey: ["emailcontentAnalytics", id],
    queryFn: () => emailClickContentAnalytics({ contentId: id }),
    enabled: ComingFrom === "email" && !isError && !!data,
  });

  const {} = useQuery({
    queryKey: ["notificationcontentAnalytics", id],
    queryFn: () => pushClickContentAnalytics({ contentId: id }),
    enabled: ComingFrom === "push" && !isError && !!data,
  });

  useEffect(() => {
    if (ComingFrom === "email" && data?.url) {
      // Instead of automatically trying to open, show the dialog
      setShowUrlDialog(true);
    }
  }, [ComingFrom, data]);

  useEffect(() => {
    // If user is not logged in and came from email, store the article ID
    const token = localStorage.getItem("token");
    if (!token && ComingFrom === "email" && id) {
      localStorage.setItem("pendingArticleId", id);
      localStorage.setItem("pendingArticleSource", ComingFrom);
    }
  }, [id, ComingFrom]);

  const handleGoBack = () => {
    navigate(`/${companyName}/dashboard`);
  };

  const handleOpenExternalUrl = () => {
    if (data?.url) {
      // Try to open in new tab
      window.open(data.url, "_blank", "noopener,noreferrer");
      setShowUrlDialog(false);
    }
  };

  const handleContinueInApp = () => {
    setShowUrlDialog(false);
    // Just close the dialog and continue viewing in the app
  };

  const handleFileClick = (file) => {
    if (
      file.contentType.startsWith("image/") ||
      file.contentType.startsWith("video/")
    ) {
      setSelectedFile(file);
    } else {
      window.open(file.url + `?${IMAGE_URL_AZURE}`, "_blank");
    }
  };

  const getImageWithToken = (url) => {
    if (!url) return "";
    return `${url}?${IMAGE_URL_AZURE}`;
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !data) {
    return (
      <Container maxWidth="lg" className="sectionCenter">
        <ErrorDisplay
          message={
            error?.response?.status === 403
              ? "You don't have permission to access this article."
              : "This article could not be found or is temporarily unavailable."
          }
          onRetry={() => refetch()}
          onGoHome={() => navigate(`/${companyName}/dashboard`)}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="sectionCenter">
      <Box sx={{ padding: isSmallScreen ? 1 : 2 }}>
        {/* Go Back Button for Iframe */}
        {isIframe && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={handleGoBack}
              sx={{
                color: primaryColor,
                "&:hover": {
                  backgroundColor: `${primaryColor}20`,
                },
                mr: 1,
              }}
            >
              <ArrowLeft size={24} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: primaryColor,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={handleGoBack}
            >
              Back to Dashboard
            </Typography>
          </Box>
        )}

        <Grid container spacing={isSmallScreen ? 2 : 3}>
          {/* Main Article Content */}
          <Grid item xs={12} md={9}>
            {/* Title and Author Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {data?.title}
              </Typography>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                sx={{ mb: 2 }}
              >
                {data?.author || ""}{" "}
                {data?.scheduledDate
                  ? format(new Date(data.scheduledDate), "dd MMM yyyy")
                  : "N/A"}
              </Typography>
            </Box>

            {/* Article Content Layout */}
            <Grid container spacing={2}>
              {/* Left Column - Image and Tags */}
              <Grid item xs={12} md={data?.mainImage ? 6 : 12}>
                {/* Main Image */}
                {data?.mainImage && (
                  <Box sx={{ mb: 2 }}>
                    <CardMedia
                      component="img"
                      image={getImageWithToken(data?.mainImage)}
                      alt="Article Image"
                      sx={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 400,
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                )}

                {/* Topic Tags */}
                {/* <Box sx={{ mb: 2 }}>
                  {data?.topic &&
                    data.topic
                      .split(",")
                      .slice(0, 3)
                      .map((el, i) => (
                        <Chip
                          key={i}
                          label={el}
                          style={{
                            marginRight: 4,
                            marginBottom: 4,
                            color: "black",
                            background: "white",
                            border: `1px solid ${primaryColor}`,
                          }}
                          onClick={() =>
                            navigate(`/${companyName}/dashboard?topic=${el}`)
                          }
                        />
                      ))}
                </Box> */}

                {/* Share Button */}
                {data?.isShearable && (
                  <Box sx={{ mb: 2 }}>
                    <ShareArticle article={data} />
                  </Box>
                )}
              </Grid>

              {/* Right Column - Article Text and Actions */}
              <Grid item xs={12} md={data?.mainImage ? 6 : 12}>
                {/* Article Text */}
                <Box sx={{ mb: 3 }}>
                  <Typography className="ql-editor" sx={quillStyles}>
                    <div
                      style={quillStyles}
                      dangerouslySetInnerHTML={{ __html: data?.text }}
                    />
                  </Typography>
                </Box>

                {/* External URL Button */}
                {data?.url && (
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      variant="contained"
                      text="View Content"
                      to={data?.url}
                      linkExternal={true}
                    />
                  </Box>
                )}

                {/* Related Files */}
                {data?.files && data.files.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.secondary", mb: 1 }}
                    >
                      Related Files:
                    </Typography>
                    <ArticleResources
                      files={data.files}
                      onFileClick={handleFileClick}
                      primaryColor={primaryColor}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Sidebar - Featured Content */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Featured
            </Typography>
            <Box>
              {data.featuredContents &&
                data.featuredContents.map((item, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 2,
                      borderRadius: 0,
                      boxShadow: "none",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate(`/${companyName}/article/details/${item.id}`)
                    }
                  >
                    <ZoomableCardMediaWithFallback
                      image={getImageWithToken(item?.mainImage)}
                      alt={`Featured ${index}`}
                      height="120"
                      title={item.title}
                      primaryColor={primaryColor}
                      isMobileCompact={isSmallScreen}
                    />
                    <CardContent style={{ background: primaryColorLight }}>
                      <Typography
                        variant="subtitle1"
                        color={getTextColor(primaryColor) || "black"}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={getTextColor(primaryColor) || "black"}
                      >
                        {item.scheduledDate &&
                          formatDistanceToNow(new Date(item.scheduledDate), {
                            addSuffix: true,
                          })}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
            </Box>
          </Grid>
        </Grid>

        {/* Related Content Section */}
        {data.relatedContents && data.relatedContents.length > 0 ? (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              You may be interested in?
            </Typography>
            <Grid container spacing={isSmallScreen ? 2 : 3}>
              {data.relatedContents.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    style={{
                      cursor: "pointer",
                      boxShadow: "none",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 0,
                      height: "300px",
                    }}
                    onClick={() =>
                      navigate(`/${companyName}/article/details/${item?.id}`)
                    }
                  >
                    <ZoomableCardMediaWithFallback
                      image={getImageWithToken(item?.mainImage)}
                      alt="Article Image"
                      height="120px"
                      title={item?.title}
                      primaryColor={primaryColor}
                      isMobileCompact={isSmallScreen}
                    />
                    <CardContent
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "130px",
                      }}
                    >
                      <Typography
                        variant="overline"
                        color="textSecondary"
                        style={{
                          color: primaryColor,
                          fontWeight: "bold",
                          fontSize: 10,
                        }}
                      >
                        {item?.topics &&
                          item.topics.split(",").slice(0, 3).join(", ")}
                      </Typography>
                      <Typography
                        variant="h5"
                        style={{
                          marginBottom: "auto",
                          fontWeight: "bold",
                          fontSize: 16,
                        }}
                      >
                        {item?.title}
                      </Typography>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {item?.scheduledDate &&
                            formatDistanceToNow(new Date(item?.scheduledDate), {
                              addSuffix: true,
                            })}
                        </Typography>
                        <hr
                          style={{
                            border: 0,
                            borderTop: "1px solid #ccc",
                            margin: "8px 0",
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}

        {/* Dialog for file preview */}
        <Dialog
          open={!!selectedFile}
          onClose={() => setSelectedFile(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent className="relative p-0">
            <button
              onClick={() => setSelectedFile(null)}
              className="absolute right-2 top-2 p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>
            {selectedFile?.contentType.startsWith("image/") ? (
              <img
                src={selectedFile.url + `?${IMAGE_URL_AZURE}`}
                alt={selectedFile.title}
                className=""
              />
            ) : selectedFile?.contentType.startsWith("video/") ? (
              <video
                src={selectedFile.url + `?${IMAGE_URL_AZURE}`}
                controls
                className=""
              />
            ) : null}
          </DialogContent>
        </Dialog>

        {/* New Dialog for handling URL actions */}
        <Dialog
          open={showUrlDialog}
          onClose={handleContinueInApp}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>How would you like to view this content?</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              This article contains external content. You can either:
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: isSmallScreen ? "column" : "row",
                gap: 2,
                marginTop: 2,
                marginBottom: 2,
              }}
            >
              <Card
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  border: `2px solid ${primaryColor}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { transform: "translateY(-5px)" },
                }}
                onClick={handleContinueInApp}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    padding: 3,
                  }}
                >
                  <FileText size={48} color={primaryColor} />
                  <Typography variant="h6" sx={{ marginTop: 2 }}>
                    Continue in Komz
                  </Typography>
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    View this content within our app
                  </Typography>
                </CardContent>
              </Card>

              <Card
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  border: `2px solid ${primaryColor}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { transform: "translateY(-5px)" },
                }}
                onClick={handleOpenExternalUrl}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    padding: 3,
                  }}
                >
                  <ExternalLink size={48} color={primaryColor} />
                  <Typography variant="h6" sx={{ marginTop: 2 }}>
                    Open External Link
                  </Typography>
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    Visit the original source
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: 2, justifyContent: "center" }}>
            <Typography variant="caption" color="textSecondary">
              You can access this content anytime from your Komz dashboard
            </Typography>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ArticlePage;

const quillStyles = {
  "& iframe, & video": {
    maxWidth: "100%",
    height: "auto",
    aspectRatio: "16/9",
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
  },
  "& p": {
    margin: "0 0 1em 0",
  },
};
